import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Check, X, Edit2, ToggleLeft, ToggleRight, ChevronDown, ChevronRight, FolderTree } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { api } from '../../../core/apiClient';
import AdminProductTree from './AdminProductTree';

interface ElementTypeRow {
  id: number;
  code: string;
  name: string;
  description?: string | null;
  sortOrder: number;
  isActive: number;
}

interface MaterialTypeRow {
  id: number;
  code: string;
  name: string;
  description?: string | null;
  elementTypeId: number | null;
  sortOrder: number;
  isActive: number;
}

function InlineInput({ value, onChange, placeholder, className = '' }: {
  value: string; onChange: (v: string) => void; placeholder?: string; className?: string;
}) {
  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`h-7 text-xs px-2 ${className}`}
    />
  );
}

const emptyForm = () => ({ code: '', name: '', description: '' });

/**
 * Arbre d'elements editable — Admin > Taules > Compres. Gestiona en un sol
 * lloc els dos nivells amb relació real a BD (Tipus d'Elements > Família
 * d'Elements, via material_types.element_type_id) i, com a secció
 * independent, Tipus de Producte (sense relació real amb els altres dos —
 * vegeu material.service.ts.generateComposedCode). Mateixes dades i
 * endpoints que les pestanyes "Tipus d'Elements"/"Família d'Elements"/
 * "Tipus de Producte": es poden seguir fent servir indistintament.
 *
 * La vista de nomes lectura dins la fitxa de cada Element (MaterialForm.tsx,
 * pestanya "Arbre d'elements") no es toca — segueix mostrant els mateixos
 * tres nivells a partir de /simple-materials/code-tree.
 */
export default function AdminElementTree({ showInactive }: { showInactive: boolean }) {
  const [elementTypes, setElementTypes] = useState<ElementTypeRow[]>([]);
  const [materialTypes, setMaterialTypes] = useState<MaterialTypeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const [etAdding, setEtAdding] = useState(false);
  const [etNewForm, setEtNewForm] = useState(emptyForm());
  const [etEditId, setEtEditId] = useState<number | null>(null);
  const [etEditForm, setEtEditForm] = useState(emptyForm());

  const [mtAddingFor, setMtAddingFor] = useState<number | null>(null);
  const [mtNewForm, setMtNewForm] = useState(emptyForm());
  const [mtEditId, setMtEditId] = useState<number | null>(null);
  const [mtEditForm, setMtEditForm] = useState(emptyForm());

  const draggingEt = useRef<number | null>(null);
  const draggingMt = useRef<MaterialTypeRow | null>(null);
  const [dragOverEt, setDragOverEt] = useState<number | null>(null);
  const [dragOverMt, setDragOverMt] = useState<number | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [etRes, mtRes] = await Promise.all([
        api.get<{ success: boolean; data: ElementTypeRow[] }>('/catalog/element-types'),
        api.get<{ success: boolean; data: MaterialTypeRow[] }>('/catalog/material-types'),
      ]);
      setElementTypes(etRes.data ?? []);
      setMaterialTypes(mtRes.data ?? []);
    } catch {
      setError('Error en carregar l\'arbre');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleExpand = (id: number) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // ── Tipus d'Elements (nivell 1) ───────────────────────────────────────────

  const etSaveNew = async () => {
    if (!etNewForm.code.trim() || !etNewForm.name.trim()) return;
    try {
      await api.post('/catalog/element-types', etNewForm);
      setEtAdding(false);
      setEtNewForm(emptyForm());
      fetchAll();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Error en crear el tipus');
    }
  };

  const etStartEdit = (item: ElementTypeRow) => {
    setEtEditId(item.id);
    setEtEditForm({ code: item.code, name: item.name, description: item.description ?? '' });
  };

  const etSaveEdit = async () => {
    if (etEditId === null) return;
    try {
      await api.put(`/catalog/element-types/${etEditId}`, etEditForm);
      setEtEditId(null);
      fetchAll();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Error en desar els canvis');
    }
  };

  const etToggleActive = async (item: ElementTypeRow) => {
    try {
      await api.put(`/catalog/element-types/${item.id}`, { isActive: item.isActive ? 0 : 1 });
      fetchAll();
    } catch { setError('Error en canviar l\'estat'); }
  };

  const etDelete = async (item: ElementTypeRow) => {
    try {
      await api.delete(`/catalog/element-types/${item.id}`);
      fetchAll();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Error en eliminar el tipus');
    }
  };

  const etDrop = async (targetId: number) => {
    const dragId = draggingEt.current;
    draggingEt.current = null;
    setDragOverEt(null);
    if (!dragId || dragId === targetId) return;
    const reordered = [...elementTypes];
    const from = reordered.findIndex((it) => it.id === dragId);
    const to = reordered.findIndex((it) => it.id === targetId);
    if (from === -1 || to === -1) return;
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setElementTypes(reordered);
    try { await api.put('/catalog/element-types/reorder', { ids: reordered.map((it) => it.id) }); }
    catch { setError('Error en desar l\'ordre'); fetchAll(); }
  };

  // ── Família d'Elements (nivell 2, dins de cada Tipus) ─────────────────────

  const mtSaveNew = async (elementTypeId: number) => {
    if (!mtNewForm.code.trim() || !mtNewForm.name.trim()) return;
    try {
      await api.post('/catalog/material-types', { ...mtNewForm, elementTypeId });
      setMtAddingFor(null);
      setMtNewForm(emptyForm());
      fetchAll();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Error en crear la família');
    }
  };

  const mtStartEdit = (item: MaterialTypeRow) => {
    setMtEditId(item.id);
    setMtEditForm({ code: item.code, name: item.name, description: item.description ?? '' });
  };

  const mtSaveEdit = async () => {
    if (mtEditId === null) return;
    try {
      await api.put(`/catalog/material-types/${mtEditId}`, mtEditForm);
      setMtEditId(null);
      fetchAll();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Error en desar els canvis');
    }
  };

  const mtToggleActive = async (item: MaterialTypeRow) => {
    try {
      await api.put(`/catalog/material-types/${item.id}`, { isActive: item.isActive ? 0 : 1 });
      fetchAll();
    } catch { setError('Error en canviar l\'estat'); }
  };

  const mtDelete = async (item: MaterialTypeRow) => {
    try {
      await api.delete(`/catalog/material-types/${item.id}`);
      fetchAll();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Error en eliminar la família');
    }
  };

  // Deixar anar sobre la capçalera d'un Tipus: la reassigna a aquest tipus
  // (es col·loca al final de les seves famílies).
  const mtDropOnElementType = async (et: ElementTypeRow) => {
    const payload = draggingMt.current;
    draggingMt.current = null;
    setDragOverEt(null);
    if (!payload || payload.elementTypeId === et.id) return;
    try {
      await api.put(`/catalog/material-types/${payload.id}`, { elementTypeId: et.id });
      fetchAll();
    } catch { setError('Error en moure la família'); }
  };

  // Deixar anar sobre una altra família: reordena i, si era d'un altre
  // Tipus, adopta el Tipus d'aquesta família destí.
  const mtDropOnMaterialType = async (target: MaterialTypeRow) => {
    const payload = draggingMt.current;
    draggingMt.current = null;
    setDragOverMt(null);
    if (!payload || payload.id === target.id) return;
    const reordered = [...materialTypes];
    const from = reordered.findIndex((m) => m.id === payload.id);
    const to = reordered.findIndex((m) => m.id === target.id);
    if (from === -1 || to === -1) return;
    const [moved] = reordered.splice(from, 1);
    const reparented = moved.elementTypeId !== target.elementTypeId;
    moved.elementTypeId = target.elementTypeId;
    reordered.splice(to, 0, moved);
    setMaterialTypes(reordered);
    try {
      if (reparented) await api.put(`/catalog/material-types/${payload.id}`, { elementTypeId: target.elementTypeId });
      await api.put('/catalog/material-types/reorder', { ids: reordered.map((m) => m.id) });
    } catch { setError('Error en desar l\'ordre'); fetchAll(); }
  };

  const visibleET = showInactive ? elementTypes : elementTypes.filter((et) => et.isActive);
  const materialTypesOf = (etId: number) => {
    const list = showInactive ? materialTypes : materialTypes.filter((mt) => mt.isActive);
    return list.filter((mt) => mt.elementTypeId === etId);
  };

  return (
    <div className="space-y-6">
      <p className="text-xs text-muted-foreground -mt-2">
        Mateixes dades que "Tipus d'Elements" i "Família d'Elements" — arrossega les famílies per reordenar-les
        o per moure-les a un altre tipus (deixa-les anar sobre la capçalera del tipus destí).
      </p>

      {error && (
        <div className="rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 flex justify-between">
          {error}
          <button onClick={() => setError(null)}><X className="h-4 w-4" /></button>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground text-center py-8">Carregant...</p>
      ) : (
        <div className="space-y-2">
          {visibleET.map((et) => {
            const isOpen = expanded.has(et.id);
            const children = materialTypesOf(et.id);
            return (
              <div key={et.id} className="rounded-xl border bg-card overflow-hidden">
                <div
                  draggable={etEditId !== et.id}
                  onDragStart={() => { draggingEt.current = et.id; }}
                  onDragOver={(e) => { e.preventDefault(); setDragOverEt(et.id); }}
                  onDrop={() => { if (draggingMt.current) mtDropOnElementType(et); else etDrop(et.id); }}
                  onDragEnd={() => { draggingEt.current = null; setDragOverEt(null); }}
                  className={`flex items-center gap-2 px-3 py-2 ${!et.isActive ? 'opacity-50' : ''} ${dragOverEt === et.id ? 'bg-blue-50' : 'bg-muted/40'}`}
                >
                  <span className="text-slate-300 cursor-grab active:cursor-grabbing select-none">⠿</span>
                  <button onClick={() => toggleExpand(et.id)} className="p-0.5 rounded hover:bg-muted">
                    {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                  {etEditId === et.id ? (
                    <>
                      <InlineInput value={etEditForm.code} onChange={(v) => setEtEditForm((f) => ({ ...f, code: v }))} placeholder="CODI" className="w-24" />
                      <InlineInput value={etEditForm.name} onChange={(v) => setEtEditForm((f) => ({ ...f, name: v }))} placeholder="Nom" className="flex-1" />
                      <button onClick={etSaveEdit} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                      <button onClick={() => setEtEditId(null)} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                    </>
                  ) : (
                    <>
                      <span className="font-mono text-xs text-muted-foreground w-10 shrink-0">{et.id}</span>
                      <span className="font-semibold text-sm flex-1">{et.name}</span>
                      <span className="text-xs text-muted-foreground">{children.length} família{children.length !== 1 ? 'es' : ''}</span>
                      <button onClick={() => etToggleActive(et)} title={et.isActive ? 'Desactivar' : 'Activar'}>
                        {et.isActive
                          ? <ToggleRight className="h-5 w-5 text-green-600" />
                          : <ToggleLeft className="h-5 w-5 text-muted-foreground" />}
                      </button>
                      <button onClick={() => etStartEdit(et)} className="p-1 hover:bg-muted rounded"><Edit2 className="h-4 w-4" /></button>
                      <button onClick={() => etDelete(et)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-4 w-4" /></button>
                    </>
                  )}
                </div>

                {isOpen && (
                  <div className="divide-y border-t bg-white">
                    {children.length === 0 && mtAddingFor !== et.id && (
                      <p className="px-4 py-3 text-xs text-muted-foreground">Sense famílies associades.</p>
                    )}
                    {children.map((mt) => (
                      <div
                        key={mt.id}
                        draggable={mtEditId !== mt.id}
                        onDragStart={() => { draggingMt.current = mt; }}
                        onDragOver={(e) => { e.preventDefault(); setDragOverMt(mt.id); }}
                        onDrop={() => mtDropOnMaterialType(mt)}
                        onDragEnd={() => { draggingMt.current = null; setDragOverMt(null); }}
                        className={`flex items-center gap-2 pl-10 pr-3 py-1.5 ${!mt.isActive ? 'opacity-50' : ''} ${dragOverMt === mt.id ? 'bg-blue-50' : ''}`}
                      >
                        <span className="text-slate-300 cursor-grab active:cursor-grabbing select-none">⠿</span>
                        {mtEditId === mt.id ? (
                          <>
                            <InlineInput value={mtEditForm.code} onChange={(v) => setMtEditForm((f) => ({ ...f, code: v }))} placeholder="CODI" className="w-24" />
                            <InlineInput value={mtEditForm.name} onChange={(v) => setMtEditForm((f) => ({ ...f, name: v }))} placeholder="Nom" className="flex-1" />
                            <button onClick={mtSaveEdit} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                            <button onClick={() => setMtEditId(null)} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                          </>
                        ) : (
                          <>
                            <span className="font-mono text-xs text-muted-foreground w-10 shrink-0">{mt.code}</span>
                            <span className="text-sm flex-1">{mt.name}</span>
                            <button onClick={() => mtToggleActive(mt)} title={mt.isActive ? 'Desactivar' : 'Activar'}>
                              {mt.isActive
                                ? <ToggleRight className="h-4 w-4 text-green-600" />
                                : <ToggleLeft className="h-4 w-4 text-muted-foreground" />}
                            </button>
                            <button onClick={() => mtStartEdit(mt)} className="p-1 hover:bg-muted rounded"><Edit2 className="h-3.5 w-3.5" /></button>
                            <button onClick={() => mtDelete(mt)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-3.5 w-3.5" /></button>
                          </>
                        )}
                      </div>
                    ))}

                    {mtAddingFor === et.id ? (
                      <div className="flex items-center gap-2 pl-10 pr-3 py-1.5 bg-blue-50/40">
                        <span className="w-4" />
                        <InlineInput value={mtNewForm.code} onChange={(v) => setMtNewForm((f) => ({ ...f, code: v }))} placeholder="CODI" className="w-24" />
                        <InlineInput value={mtNewForm.name} onChange={(v) => setMtNewForm((f) => ({ ...f, name: v }))} placeholder="Nom" className="flex-1" />
                        <button onClick={() => mtSaveNew(et.id)} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                        <button onClick={() => { setMtAddingFor(null); setMtNewForm(emptyForm()); }} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                      </div>
                    ) : (
                      <div className="pl-10 pr-3 py-1.5">
                        <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => { setMtAddingFor(et.id); setMtEditId(null); }}>
                          <Plus className="h-3.5 w-3.5 mr-1" /> Nova família sota {et.name}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {etAdding ? (
        <div className="rounded-xl border bg-blue-50/40 px-3 py-2 flex items-center gap-2">
          <InlineInput value={etNewForm.code} onChange={(v) => setEtNewForm((f) => ({ ...f, code: v }))} placeholder="CODI" className="w-32" />
          <InlineInput value={etNewForm.name} onChange={(v) => setEtNewForm((f) => ({ ...f, name: v }))} placeholder="Nom" className="flex-1" />
          <button onClick={etSaveNew} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
          <button onClick={() => { setEtAdding(false); setEtNewForm(emptyForm()); }} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
        </div>
      ) : (
        <Button size="sm" variant="outline" onClick={() => { setEtAdding(true); setEtEditId(null); }}>
          <Plus className="h-4 w-4 mr-1" /> Nou Tipus d'Element
        </Button>
      )}

      <div className="pt-4 border-t">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-2">
          <FolderTree className="h-4 w-4" /> Tipus de Producte
        </h3>
        <p className="text-xs text-muted-foreground mb-3">
          Família {'>'} Subfamília {'>'} Producte {'>'} Model — estructura pròpia de codificació de
          productes, sense relació amb Tipus/Família d'Elements. La Família es combina amb ells al
          codi de l'element (p.ex. 1.120.0.1); Subfamília/Producte/Model son nomes catàleg.
        </p>
        <AdminProductTree showInactive={showInactive} />
      </div>
    </div>
  );
}
